'use client'

import { MotionConfig } from 'framer-motion'
import * as React from 'react'

/**
 * Shared plumbing for the animated line/segmented tab indicators. Both variants
 * use a single shared-layout `motion` element per group that slides to the active
 * Radix trigger, so the layout-id scoping, reduced-motion handling, ref merging,
 * and `data-state` observer live here to keep the two components in sync.
 *
 * Each variant passes `layoutDependency={value}` on its indicator, gating the
 * layout animation on a selection change: the indicator slides between triggers
 * (their values differ) but never animates when the list itself moves on screen
 * (MKT-1557) — layout shifts move it instantly with its trigger.
 */

const LayoutIdContext = React.createContext<string | null>(null)

// `useLayoutEffect` on the client so the active marker is present on the first
// painted frame (the selected tab never renders without its indicator); plain
// `useEffect` on the server to avoid React's SSR warning.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

/**
 * Wrap a tab list so its triggers share one `layoutId` (scoped per group via
 * `useId`) and honour `prefers-reduced-motion` — reduced-motion users get an
 * instant swap instead of the slide.
 */
export function AnimatedTabIndicatorProvider({ children }: { children: React.ReactNode }) {
  const layoutId = React.useId()
  return (
    <LayoutIdContext.Provider value={layoutId}>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LayoutIdContext.Provider>
  )
}

/**
 * Mirrors a Radix trigger's `data-state` into React state so the shared indicator
 * can mount on the active trigger, and merges the observer ref with a forwarded
 * ref so consumers can still pass their own.
 */
export function useActiveTabIndicator(externalRef: React.Ref<HTMLButtonElement> | undefined) {
  const layoutId = React.useContext(LayoutIdContext)
  const ref = React.useRef<HTMLButtonElement>(null)
  const [isActive, setIsActive] = React.useState(false)

  const mergedRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      ref.current = node

      if (typeof externalRef === 'function') {
        externalRef(node)
      } else if (externalRef) {
        externalRef.current = node
      }
    },
    [externalRef]
  )

  useIsomorphicLayoutEffect(() => {
    const element = ref.current

    if (!element) return

    const update = () => setIsActive(element.getAttribute('data-state') === 'active')

    update()

    const observer = new MutationObserver(update)
    observer.observe(element, { attributes: true, attributeFilter: ['data-state'] })

    return () => observer.disconnect()
  }, [])

  return { layoutId, isActive, ref: mergedRef }
}
